import { c as _c } from "react/compiler-runtime";
import { useFrontWeightDesign, useStrikeWeightDesign, useStrikeWeightRatioDesign, useWippenSupportSpringsDesign } from '@/hooks/store/use-design-store';
import { useStrikeWeightTargetSerie } from '../strike-weight/hooks/use-strike-weight-target-serie';
import { useStrikeWeightRatioTargetSerie } from '../strike-weight-ratio/hooks/strike-weight-ratio-target-serie';
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { useWippenSupportSpringsTargetSerie } from '../wippen-support-springs/hooks/use-wippen-support-springs-target-serie';
import { FrontWeightDesignMode } from '@/lib/piano/touch-design/design/front-weight-design';
import { useMemo } from 'react';
import type { TouchDesignSerie } from '@/components/charts/interfaces';
import { useFrontWeightTargetSerie } from '../front-weight/hooks/use-front-weight-target-serie';
import { TouchDesignSerieVariant } from '@/components/charts/KeyboardChart';
import { useGenerateSerie } from '@/components/charts/hooks/use-generate-serie';
import { useTranslation } from '@/hooks/use-translation';
import { StrikeWeightDesignMode } from '@/lib/piano/touch-design/design/strike-weight-design';
export interface TargetSeries {
  frontWeightTargetSerie: TouchDesignSerie;
  strikeWeightTargetSerie: TouchDesignSerie;
  strikeWeightRatioTargetSerie: TouchDesignSerie;
  supportSpringBalanceWeightTargetSerie: TouchDesignSerie;
}

/**
 * The four target series of the design, from the stored modes and targets.
 * Built once for the page by `KeyboardAnalysisProvider`; read them with
 * `useTargetSeries`.
 */
export const useGenerateTargetSeries = keyboard => {
  const $ = _c(61);
  const {
    t
  } = useTranslation();
  const {
    frontWeightDesignMode,
    frontWeightDesignTarget
  } = useFrontWeightDesign();
  const {
    strikeWeightDesignMode,
    strikeWeightDesignTarget
  } = useStrikeWeightDesign();
  const {
    strikeWeightRatioDesignMode,
    strikeWeightRatioDesignTarget
  } = useStrikeWeightRatioDesign();
  const {
    wippenSupportSpringsDesignMode,
    wippenSupportSpringsDesignTarget
  } = useWippenSupportSpringsDesign();
  const {
    generateSerie
  } = useGenerateSerie(keyboard);
  const {
    generateStrikeWeightRatioTargetSerie
  } = useStrikeWeightRatioTargetSerie(keyboard);
  const {
    generateWippenSupportSpringTargetSerie
  } = useWippenSupportSpringsTargetSerie(keyboard);
  const {
    generateFrontWeightTargetSerie
  } = useFrontWeightTargetSerie(keyboard);
  const {
    generateStrikeWeightTargetSerie
  } = useStrikeWeightTargetSerie(keyboard);
  let t0;
  if ($[0] !== generateSerie || $[1] !== t) {
    t0 = generateSerie(_temp, t("Target"), TouchDesignSerieVariant.Target);
    $[0] = generateSerie;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const nullTargetSerie = t0;
  let t1;
  if ($[3] !== generateStrikeWeightRatioTargetSerie || $[4] !== strikeWeightRatioDesignMode || $[5] !== strikeWeightRatioDesignTarget) {
    t1 = generateStrikeWeightRatioTargetSerie(strikeWeightRatioDesignMode, strikeWeightRatioDesignTarget);
    $[3] = generateStrikeWeightRatioTargetSerie;
    $[4] = strikeWeightRatioDesignMode;
    $[5] = strikeWeightRatioDesignTarget;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const strikeWeightRatioTargetSerie = t1;
  let t2;
  if ($[7] !== generateWippenSupportSpringTargetSerie || $[8] !== wippenSupportSpringsDesignMode || $[9] !== wippenSupportSpringsDesignTarget) {
    t2 = generateWippenSupportSpringTargetSerie(wippenSupportSpringsDesignMode, wippenSupportSpringsDesignTarget);
    $[7] = generateWippenSupportSpringTargetSerie;
    $[8] = wippenSupportSpringsDesignMode;
    $[9] = wippenSupportSpringsDesignTarget;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const supportSpringBalanceWeightTargetSerie = t2;
  let t3;
  bb0: {
    if (frontWeightDesignMode === FrontWeightDesignMode.Computed) {
      let strikeWeightTargetSerie;
      let t4;
      if ($[11] !== frontWeightDesignMode || $[12] !== frontWeightDesignTarget || $[13] !== generateFrontWeightTargetSerie || $[14] !== generateStrikeWeightTargetSerie || $[15] !== nullTargetSerie || $[16] !== strikeWeightDesignMode || $[17] !== strikeWeightDesignTarget || $[18] !== strikeWeightRatioTargetSerie || $[19] !== supportSpringBalanceWeightTargetSerie) {
        strikeWeightTargetSerie = generateStrikeWeightTargetSerie(strikeWeightDesignMode, strikeWeightDesignTarget, nullTargetSerie, strikeWeightRatioTargetSerie, supportSpringBalanceWeightTargetSerie);
        t4 = generateFrontWeightTargetSerie(frontWeightDesignMode, frontWeightDesignTarget, strikeWeightTargetSerie, strikeWeightRatioTargetSerie, supportSpringBalanceWeightTargetSerie);
        $[11] = frontWeightDesignMode;
        $[12] = frontWeightDesignTarget;
        $[13] = generateFrontWeightTargetSerie;
        $[14] = generateStrikeWeightTargetSerie;
        $[15] = nullTargetSerie;
        $[16] = strikeWeightDesignMode;
        $[17] = strikeWeightDesignTarget;
        $[18] = strikeWeightRatioTargetSerie;
        $[19] = supportSpringBalanceWeightTargetSerie;
        $[20] = strikeWeightTargetSerie;
        $[21] = t4;
      } else {
        strikeWeightTargetSerie = $[20];
        t4 = $[21];
      }
      let t5;
      if ($[22] !== strikeWeightTargetSerie || $[23] !== t4) {
        t5 = {
          frontWeightTargetSerie: t4,
          strikeWeightTargetSerie
        };
        $[22] = strikeWeightTargetSerie;
        $[23] = t4;
        $[24] = t5;
      } else {
        t5 = $[24];
      }
      t3 = t5;
      break bb0;
    }
    if (strikeWeightDesignMode === StrikeWeightDesignMode.Computed) {
      let t4;
      let t5;
      if ($[25] !== frontWeightDesignMode || $[26] !== frontWeightDesignTarget || $[27] !== generateFrontWeightTargetSerie || $[28] !== generateStrikeWeightTargetSerie || $[29] !== nullTargetSerie || $[30] !== strikeWeightDesignMode || $[31] !== strikeWeightDesignTarget || $[32] !== strikeWeightRatioTargetSerie || $[33] !== supportSpringBalanceWeightTargetSerie) {
        const frontWeightTargetSerie = generateFrontWeightTargetSerie(frontWeightDesignMode, frontWeightDesignTarget, nullTargetSerie, strikeWeightRatioTargetSerie, supportSpringBalanceWeightTargetSerie);
        t4 = frontWeightTargetSerie;
        t5 = generateStrikeWeightTargetSerie(strikeWeightDesignMode, strikeWeightDesignTarget, frontWeightTargetSerie, strikeWeightRatioTargetSerie, supportSpringBalanceWeightTargetSerie);
        $[25] = frontWeightDesignMode;
        $[26] = frontWeightDesignTarget;
        $[27] = generateFrontWeightTargetSerie;
        $[28] = generateStrikeWeightTargetSerie;
        $[29] = nullTargetSerie;
        $[30] = strikeWeightDesignMode;
        $[31] = strikeWeightDesignTarget;
        $[32] = strikeWeightRatioTargetSerie;
        $[33] = supportSpringBalanceWeightTargetSerie;
        $[34] = t4;
        $[35] = t5;
      } else {
        t4 = $[34];
        t5 = $[35];
      }
      let t6;
      if ($[36] !== t4 || $[37] !== t5) {
        t6 = {
          frontWeightTargetSerie: t4,
          strikeWeightTargetSerie: t5
        };
        $[36] = t4;
        $[37] = t5;
        $[38] = t6;
      } else {
        t6 = $[38];
      }
      t3 = t6;
      break bb0;
    }
    let t4;
    if ($[39] !== frontWeightDesignMode || $[40] !== frontWeightDesignTarget || $[41] !== generateFrontWeightTargetSerie || $[42] !== nullTargetSerie || $[43] !== strikeWeightRatioTargetSerie || $[44] !== supportSpringBalanceWeightTargetSerie) {
      t4 = generateFrontWeightTargetSerie(frontWeightDesignMode, frontWeightDesignTarget, nullTargetSerie, strikeWeightRatioTargetSerie, supportSpringBalanceWeightTargetSerie);
      $[39] = frontWeightDesignMode;
      $[40] = frontWeightDesignTarget;
      $[41] = generateFrontWeightTargetSerie;
      $[42] = nullTargetSerie;
      $[43] = strikeWeightRatioTargetSerie;
      $[44] = supportSpringBalanceWeightTargetSerie;
      $[45] = t4;
    } else {
      t4 = $[45];
    }
    let t5;
    if ($[46] !== generateStrikeWeightTargetSerie || $[47] !== nullTargetSerie || $[48] !== strikeWeightDesignMode || $[49] !== strikeWeightDesignTarget || $[50] !== strikeWeightRatioTargetSerie || $[51] !== supportSpringBalanceWeightTargetSerie) {
      t5 = generateStrikeWeightTargetSerie(strikeWeightDesignMode, strikeWeightDesignTarget, nullTargetSerie, strikeWeightRatioTargetSerie, supportSpringBalanceWeightTargetSerie);
      $[46] = generateStrikeWeightTargetSerie;
      $[47] = nullTargetSerie;
      $[48] = strikeWeightDesignMode;
      $[49] = strikeWeightDesignTarget;
      $[50] = strikeWeightRatioTargetSerie;
      $[51] = supportSpringBalanceWeightTargetSerie;
      $[52] = t5;
    } else {
      t5 = $[52];
    }
    let t6;
    if ($[53] !== t4 || $[54] !== t5) {
      t6 = {
        frontWeightTargetSerie: t4,
        strikeWeightTargetSerie: t5
      };
      $[53] = t4;
      $[54] = t5;
      $[55] = t6;
    } else {
      t6 = $[55];
    }
    t3 = t6;
  }
  const {
    frontWeightTargetSerie: frontWeightTargetSerie_0,
    strikeWeightTargetSerie: strikeWeightTargetSerie_0
  } = t3;
  let t4;
  if ($[56] !== frontWeightTargetSerie_0 || $[57] !== strikeWeightRatioTargetSerie || $[58] !== strikeWeightTargetSerie_0 || $[59] !== supportSpringBalanceWeightTargetSerie) {
    t4 = {
      frontWeightTargetSerie: frontWeightTargetSerie_0,
      strikeWeightRatioTargetSerie,
      strikeWeightTargetSerie: strikeWeightTargetSerie_0,
      supportSpringBalanceWeightTargetSerie
    };
    $[56] = frontWeightTargetSerie_0;
    $[57] = strikeWeightRatioTargetSerie;
    $[58] = strikeWeightTargetSerie_0;
    $[59] = supportSpringBalanceWeightTargetSerie;
    $[60] = t4;
  } else {
    t4 = $[60];
  }
  return t4;
};
function _temp() {
  return null;
}